import React, {
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
  useRef,
  useState,
} from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import {
  usePortalCodeSignInMutation,
  usePortalSendCodeMutation,
} from "../../../slices/PortalSlice";
import {
  PortalAlert,
  PortalAuthFrame,
  portalLink,
  portalPrimary,
} from "../components/PortalUi";
import { cleanCode, errorMessage } from "../portal.utils";
import { getRememberedFirm, setPortalSession } from "../portalSession";

const MailIcon = () => (
  <svg
    width={"24"}
    height={"24"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}
    aria-hidden={"true"}>
    <rect x={"3"} y={"5"} width={"18"} height={"14"} rx={"2"} />
    <path d={"m3 7 9 6 9-6"} />
  </svg>
);

/** "Check your email": type the 6-digit code. */
export const PortalCode = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") ?? "";
  // Where to go after the code (e.g. a signing page); only portal pages.
  const nextParam = params.get("next") ?? "";
  const nextPage = /^\/portal\/[\w\-/]+$/.test(nextParam)
    ? nextParam
    : "/portal/home";
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const [signIn, signInState] = usePortalCodeSignInMutation();
  const [sendCode, sendCodeState] = usePortalSendCodeMutation();

  if (!email) return <Navigate to={"/portal"} replace={true} />;

  const fill = (start: number, value: string) => {
    const clean = cleanCode(value);
    if (!clean) return;
    const next = [...digits];
    clean.split("").forEach((d, i) => {
      if (start + i < 6) next[start + i] = d;
    });
    setDigits(next);
    inputs.current[Math.min(start + clean.length, 5)]?.focus();
  };

  const onChange = (index: number, value: string) => {
    if (value === "") {
      const next = [...digits];
      next[index] = "";
      setDigits(next);
      return;
    }
    fill(index, value);
  };

  const onKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !digits[index] && index > 0)
      inputs.current[index - 1]?.focus();
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    fill(0, event.clipboardData.getData("text"));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const code = digits.join("");
    if (code.length !== 6) return setError("Enter all 6 digits of the code.");
    const result = await signIn({ email, code });
    if ("error" in result) return setError(errorMessage(result.error));
    setPortalSession(result.data);
    navigate(nextPage, { replace: true });
  };

  const resend = async () => {
    setError(null);
    setResent(false);
    const result = await sendCode({ email });
    if ("error" in result) return setError(errorMessage(result.error));
    setDigits(["", "", "", "", "", ""]);
    setResent(true);
    inputs.current[0]?.focus();
  };

  return (
    <PortalAuthFrame firmName={getRememberedFirm()}>
      <span
        className={
          "flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#F1EEFC] text-[#6B4EFF]"
        }>
        <MailIcon />
      </span>
      <div className={"flex flex-col gap-1.5"}>
        <h1 className={"m-0 text-2xl font-extrabold tracking-[-0.4px]"}>
          Check your email
        </h1>
        <span className={"text-sm font-medium leading-relaxed text-[#6B7185]"}>
          If <strong className={"text-[#1F2433]"}>{email}</strong> has access to
          a client portal, we sent it a 6-digit code.
        </span>
      </div>

      <form
        className={"flex flex-col gap-[22px]"}
        onSubmit={submit}
        noValidate={true}>
        <div className={"flex flex-col gap-2"}>
          <label
            htmlFor={"portal-code-0"}
            className={"text-[13px] font-bold text-[#3A4256]"}>
            Code
          </label>
          <div className={"grid grid-cols-6 gap-2"}>
            {digits.map((digit, i) => (
              <input
                key={i}
                id={`portal-code-${i}`}
                ref={(el) => (inputs.current[i] = el)}
                aria-label={`Digit ${i + 1}`}
                inputMode={"numeric"}
                autoComplete={i === 0 ? "one-time-code" : "off"}
                maxLength={i === 0 ? 6 : 1}
                value={digit}
                autoFocus={i === 0}
                onChange={(e) => onChange(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                onPaste={onPaste}
                className={`h-[54px] w-full min-w-0 rounded-[10px] border-[1.5px] text-center text-[22px] font-extrabold text-[#1F2433] outline-none focus:border-[#6B4EFF] ${
                  digit ? "border-[#6B4EFF]" : "border-[#DCE2EA]"
                }`}
              />
            ))}
          </div>
        </div>
        {error && <PortalAlert>{error}</PortalAlert>}
        {resent && !error && (
          <PortalAlert tone={"success"}>
            We sent a new code. Use the newest one.
          </PortalAlert>
        )}
        <button
          type={"submit"}
          className={portalPrimary}
          disabled={signInState.isLoading}>
          {signInState.isLoading ? "Checking…" : "Continue"}
        </button>
      </form>

      <div
        className={
          "flex flex-wrap justify-between gap-2 text-[13px] font-semibold text-[#6B7185]"
        }>
        <span>
          Didn’t get it?{" "}
          <button
            type={"button"}
            className={portalLink}
            onClick={resend}
            disabled={sendCodeState.isLoading}>
            {sendCodeState.isLoading ? "Sending…" : "Send a new code"}
          </button>
        </span>
        <Link to={"/portal"} className={portalLink}>
          Use password instead
        </Link>
      </div>
    </PortalAuthFrame>
  );
};
